import DashboardIcon from "@mui/icons-material/Dashboard";
import PersonIcon from "@mui/icons-material/Person";
import PostAddIcon from "@mui/icons-material/PostAdd";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import PeopleIcon from "@mui/icons-material/People";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import SettingsIcon from "@mui/icons-material/Settings";
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import TuneIcon from "@mui/icons-material/Tune";
import AssignmentIcon from "@mui/icons-material/Assignment";
import HistoryIcon from "@mui/icons-material/History";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import PsychologyIcon from "@mui/icons-material/Psychology";

import { ROLES, normalizeRole } from "./roles";

/**
 * Sidebar menus, home pages and page access per TalentEngine role.
 *
 * Each link:
 *   id       - unique key
 *   text     - label in the sidebar
 *   path     - where the link goes
 *   basePath - (optional) path that grants access to the link's pages when it
 *              differs from `path`; defaults to `path`
 *   section  - (optional) "bottom" to place it under the divider
 *
 * A role may open a link's path and every page below it (e.g. Clients also
 * allows /clients/addClient).
 *
 * Which pages a role sees comes from Intelliconvo (see useSidebarMenu); the
 * order of ROLE_MENUS below is always the sidebar order, and ROLE_MENUS is
 * the whole menu when Intelliconvo's menus are unavailable.
 */
const LINKS = Object.freeze({
  SUPPLIER_DASHBOARD: { id: "supplier-dashboard", text: "Dashboard", path: "/supplierdashboard", icon: <DashboardIcon /> },
  CLIENTS: { id: "clients", text: "Clients", path: "/clients", icon: <SupervisorAccountIcon /> },
  SUPPLIERS: { id: "suppliers", text: "Suppliers", path: "/suppliermanagement", icon: <PersonIcon /> },
  PIPELINE_CONFIG: { id: "pipeline-config", text: "Pipeline Config", path: "/pipeline-config", icon: <SettingsIcon /> },
  OFFER_MANAGEMENT: { id: "offer-management", text: "Offer Management", path: "/offermanagement", icon: <AssignmentTurnedInIcon /> },
  SEARCH_CONFIG: { id: "search-config", text: "Search Config", path: "/cv_search_config", icon: <TuneIcon /> },
  PROMPTS: { id: "prompts", text: "Prompt Management", path: "/prompts", icon: <PsychologyIcon /> },
  AUDIT: { id: "audit", text: "Audit", path: "/settings/audit-logs", basePath: "/settings", icon: <HistoryIcon /> },
  SETTINGS: { id: "settings", text: "Settings", path: "/settings/job-document-config", basePath: "/settings", icon: <SettingsIcon />, section: "bottom" },
  REQUISITIONS: { id: "requisitions", text: "Requisition", path: "/requisitions", icon: <AssignmentIcon /> },
  APPROVALS: { id: "approvals", text: "Approvals", path: "/approvals", icon: <FactCheckIcon /> },
  JOB_ASSIGNMENTS: { id: "job-assignments", text: "Job Assignments", path: "/job-assignments", icon: <AssignmentIndIcon /> },
  OFFERS: { id: "offers", text: "Offers", path: "/offers", icon: <LocalOfferIcon /> },
  MY_JOBS: { id: "my-jobs", text: "My Jobs", path: "/jobs", icon: <PostAddIcon /> },
  INTERVIEW_TRACKING: { id: "interview-tracking", text: "Interview Tracking", path: "/interview_info", icon: <RecordVoiceOverIcon /> },
  ANALYTICS: { id: "analytics", text: "Analytics", path: "/analytics", icon: <AnalyticsIcon /> },
  CANDIDATES: { id: "candidates", text: "Candidates", path: "/candidate_data", icon: <PeopleIcon /> },
  SUPPLIER_CANDIDATES: { id: "supplier-candidates", text: "Candidate", path: "/candidate_data", icon: <PeopleIcon /> },
  CV_SEARCH: { id: "cv-search", text: "CV Search", path: "/cv_search", icon: <ManageSearchIcon /> },
  SKILLS: { id: "skills", text: "Skills", path: "/skills", icon: <PlaylistAddIcon /> },
  SUPPORT: { id: "support", text: "Support", path: "/support", icon: <HelpOutlineIcon /> },
});

export const ROLE_MENUS = Object.freeze({
  [ROLES.ADMIN]: [
    LINKS.CLIENTS,
    LINKS.SUPPLIERS,
    LINKS.PIPELINE_CONFIG,
    LINKS.OFFER_MANAGEMENT,
    LINKS.SEARCH_CONFIG,
    LINKS.PROMPTS,
    LINKS.AUDIT,
    LINKS.SUPPORT,
    LINKS.SETTINGS,
  ],
  [ROLES.MANAGER]: [
    LINKS.APPROVALS,
    LINKS.JOB_ASSIGNMENTS,
    LINKS.INTERVIEW_TRACKING,
    LINKS.OFFERS,
    LINKS.ANALYTICS,
    LINKS.SUPPORT,
  ],
  [ROLES.RECRUITER]: [
    LINKS.REQUISITIONS,
    LINKS.MY_JOBS,
    LINKS.CANDIDATES,
    LINKS.INTERVIEW_TRACKING,
    LINKS.CV_SEARCH,
    LINKS.OFFER_MANAGEMENT,
    LINKS.SKILLS,
    LINKS.SUPPORT,
  ],
  [ROLES.SUPPLIER]: [
    LINKS.SUPPLIER_DASHBOARD,
    LINKS.SUPPLIER_CANDIDATES,
    LINKS.CV_SEARCH,
    LINKS.SUPPORT,
  ],
});

// Landing page after login.
export const ROLE_HOME = Object.freeze({
  [ROLES.ADMIN]: LINKS.CLIENTS.path,
  [ROLES.MANAGER]: LINKS.APPROVALS.path,
  [ROLES.RECRUITER]: LINKS.MY_JOBS.path,
  [ROLES.SUPPLIER]: LINKS.SUPPLIER_DASHBOARD.path,
});

// Pages outside the menus that a role reaches from its own pages.
export const ROLE_EXTRA_PATHS = Object.freeze({
  // Managers view candidates (read-only) from jobs and interview tracking.
  [ROLES.MANAGER]: ["/candidate_data/Viewcandidate"],
  // Recruiters open a job's candidates from My Jobs ("View candidates").
  [ROLES.RECRUITER]: ["/admindashboard/adminAccordion"],
});

// Pages every signed-in role may open: home redirect, own profile, and the
// resume/file viewers opened from CV Search and candidate pages.
export const SHARED_PATHS = Object.freeze(["/", "/profile", "/admindashboard/viewFile", "/candidate_data/viewFile"]);

// Sidebar labels that differ for one role. They win over the Intelliconvo
// menu name, because Intelliconvo shares one menu item (and name) across roles:
// e.g. Admins only manage templates on the Offer Management page.
const ROLE_LINK_LABELS = Object.freeze({
  [ROLES.ADMIN]: Object.freeze({ [LINKS.OFFER_MANAGEMENT.id]: "Offer Templates" }),
});

/** The role's own label for a link, if it has one. */
const roleLabel = (role, link) => ROLE_LINK_LABELS[normalizeRole(role)]?.[link.id];

/**
 * Built-in sidebar links for a role (empty for an unknown role) — used when
 * the menus from Intelliconvo are not available.
 */
export const getMenuForRole = (role) =>
  (ROLE_MENUS[normalizeRole(role)] || []).map((link) => {
    const label = roleLabel(role, link);
    return label ? { ...link, text: label } : link;
  });

/**
 * Position of a link in the role's sidebar: its place in the role's built-in
 * menu (ROLE_MENUS). A page Intelliconvo grants that the built-in menu does
 * not list goes after the role's other pages but before Support, which stays
 * last.
 */
const menuRank = (ownLinks, link) => {
  const index = ownLinks.findIndex((l) => l.id === link.id);
  if (index !== -1) return index;
  const supportIndex = ownLinks.findIndex((l) => l.id === LINKS.SUPPORT.id);
  return supportIndex !== -1 ? supportIndex - 0.5 : ownLinks.length;
};

/**
 * Sidebar links from the menus managed in Intelliconvo (GET /api/users/sidebar-menu).
 *
 * Intelliconvo decides which pages a role gets, their labels and
 * permissions. The order is ours: links follow the role's built-in menu
 * (ROLE_MENUS), so Intelliconvo's menu_order is ignored. Intelliconvo keeps
 * one order per menu item for all roles, so it can't express a per-role order.
 *
 * Each menu is matched to one of our pages by its route — the role's own
 * built-in link first, so e.g. "/candidate_data" keeps the supplier's link for
 * suppliers. The page keeps our icon, basePath and section. Routes we have
 * no page for are dropped and reported in `unknownRoutes`.
 *
 * @param {string} role - TalentEngine role.
 * @param {Array} remoteMenus - [{ name, route, permissions }].
 * @returns {{ links: Array, unknownRoutes: string[] }}
 */
export const buildMenuFromIntelliconvo = (role, remoteMenus) => {
  const ownLinks = getMenuForRole(role);
  const allLinks = Object.values(LINKS);
  const links = [];
  const unknownRoutes = [];

  (Array.isArray(remoteMenus) ? remoteMenus : []).forEach((menu) => {
    const route = menu?.route;
    const link = ownLinks.find((l) => l.path === route) || allLinks.find((l) => l.path === route);
    if (!link) {
      unknownRoutes.push(route);
      return;
    }
    if (links.some((l) => l.id === link.id)) return; // same page listed twice
    links.push({ ...link, text: roleLabel(role, link) || menu.name || link.text, permissions: menu.permissions || [] });
  });

  // Array.prototype.sort is stable: extra pages keep Intelliconvo's order among themselves.
  links.sort((a, b) => menuRank(ownLinks, a) - menuRank(ownLinks, b));

  return { links, unknownRoutes };
};

/**
 * Home page for a role, or null for an unknown role. With `menu` (the links
 * in the sidebar), the role's usual home is used only while it is in the
 * menu; otherwise the first menu page is home.
 */
export const getHomeForRole = (role, menu = null) => {
  const home = ROLE_HOME[normalizeRole(role)] || null;
  if (!home || !Array.isArray(menu) || !menu.length) return home;
  return menu.some((link) => link.path === home) ? home : menu[0].path;
};

const matchesPath = (pathname, allowed) => pathname === allowed || pathname.startsWith(`${allowed}/`);

/**
 * Whether `role` may open `pathname` (a menu page, a page below one, or a
 * shared page). `menu` is the sidebar in use; the built-in menu by default.
 */
export const isPathAllowedForRole = (role, pathname, menu = null) => {
  const normalized = normalizeRole(role);
  if (!normalized) return false;
  if (SHARED_PATHS.includes(pathname)) return true;
  const links = Array.isArray(menu) && menu.length ? menu : getMenuForRole(normalized);
  const allowed = [
    ...links.map((link) => link.basePath || link.path),
    ...(ROLE_EXTRA_PATHS[normalized] || []),
  ];
  return allowed.some((path) => matchesPath(pathname, path));
};
